export const Textarea = ({
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  isFocused = false,
  className = "",
  rows = 4,
  cols,
  resize = "vertical",
  disabled = false,
  readOnly = false,
  maxLength,
  minLength,
  // Color customization
  borderColor = "var(--gray, #d1d5db)",
  focusBorderColor = "var(--primary, #ff5a00)",
  focusShadowColor = "rgba(255, 90, 0, 0.2)",
  backgroundColor = "var(--background, #ffffff)",
  textColor = "var(--text, #000000)",
  placeholderColor = "var(--icon, #687076)",
  errorBorderColor = "var(--error, #ff003c)",
  disabledBackgroundColor = "var(--light-gray, #f5f5f5)",
  // Position customization
  padding = "12px",
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  margin = "0",
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  // Size customization
  width = "100%",
  minWidth,
  maxWidth,
  height,
  minHeight = "100px",
  maxHeight,
  // Border customization
  borderRadius = "6px",
  borderWidth = "1px",
  borderStyle = "solid",
  // Typography customization
  fontSize = "14px",
  fontWeight = "400",
  lineHeight = "1.5",
  fontFamily = "inherit",
  // State
  hasError = false,
  ...props
}) => {
  const computedPadding =
    paddingTop || paddingRight || paddingBottom || paddingLeft
      ? `${paddingTop || padding} ${paddingRight || padding} ${paddingBottom || padding} ${paddingLeft || padding}`
      : padding;

  const computedMargin =
    marginTop || marginRight || marginBottom || marginLeft
      ? `${marginTop || margin} ${marginRight || margin} ${marginBottom || margin} ${marginLeft || margin}`
      : margin;

  const getResizeValue = () => {
    switch (resize) {
      case "none":
        return "none";
      case "both":
        return "both";
      case "horizontal":
        return "horizontal";
      case "vertical":
      default:
        return "vertical";
    }
  };

  const textareaStyles = {
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,
    padding: computedPadding,
    margin: computedMargin,
    borderRadius,
    border: `${borderWidth} ${borderStyle} ${hasError ? errorBorderColor : borderColor}`,
    fontSize,
    fontWeight,
    lineHeight,
    fontFamily,
    boxSizing: "border-box",
    outline: "none",
    resize: getResizeValue(),
    backgroundColor: disabled ? disabledBackgroundColor : backgroundColor,
    color: textColor,
    cursor: disabled ? "not-allowed" : "text",
    opacity: disabled ? 0.6 : 1,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    verticalAlign: "top",
    ...(isFocused &&
      !disabled && {
        borderColor: hasError ? errorBorderColor : focusBorderColor,
        boxShadow: `0 0 0 2px ${hasError ? "rgba(255, 0, 60, 0.2)" : focusShadowColor}`,
      }),
  };

  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      rows={rows}
      cols={cols}
      disabled={disabled}
      readOnly={readOnly}
      maxLength={maxLength}
      minLength={minLength}
      style={textareaStyles}
      className={className}
      {...props}
    />
  );
};

// Textarea with character counter
export const TextareaWithCounter = ({
  value = "",
  maxLength,
  showCounter = true,
  counterPosition = "bottom-right",
  counterColor = "var(--icon, #687076)",
  counterFontSize = "12px",
  ...props
}) => {
  const wrapperStyles = {
    position: "relative",
    width: props.width || "100%",
  };

  const getCounterPositionStyles = () => {
    const base = {
      position: "absolute",
      fontSize: counterFontSize,
      color: counterColor,
      pointerEvents: "none",
      padding: "4px 8px",
    };

    switch (counterPosition) {
      case "top-left":
        return { ...base, top: "-20px", left: "0" };
      case "top-right":
        return { ...base, top: "-20px", right: "0" };
      case "bottom-left":
        return { ...base, bottom: "8px", left: "8px" };
      case "bottom-right":
      default:
        return { ...base, bottom: "8px", right: "8px" };
    }
  };

  const currentLength = value?.length || 0;
  const isOverLimit = maxLength && currentLength > maxLength;

  return (
    <div style={wrapperStyles}>
      <Textarea value={value} maxLength={maxLength} {...props} />
      {showCounter && maxLength && (
        <span
          style={{
            ...getCounterPositionStyles(),
            color: isOverLimit ? "var(--error, #ff003c)" : counterColor,
          }}
        >
          {currentLength}/{maxLength}
        </span>
      )}
    </div>
  );
};
